import { Component, Input, Output, EventEmitter } from '@angular/core';
import { ComponentType } from '@angular/cdk/portal';
import { I18NDescriptor } from '../../uikit.interface';
import { MatDialog } from '@angular/material/dialog';

export interface VaFilterChipDialogConfig {
  component: ComponentType<any>;
  width?: string;
  height?: string;
}

export interface VaFilterChipConfig {
  id: string; // Used to differentiate filters and chips.
  title: I18NDescriptor; // Title for the chip text.
  data: object; // Initial data to send to the dialog
  canDisable?: boolean; // Whether or not to allow removal of this filter. Shows delete button.
  enabled?: boolean; // If true, the component will be turned on. Good for initial setup.
  icon?: string; // Icon to show on the leftside of the chip.
  text?: I18NDescriptor; // Value text to show for the chip.
  dialog?: VaFilterChipDialogConfig;
}

export interface VaFilterChipContainerConfig {
  canAddAndRemove?: boolean; // Used to turn off and on UI for adding and removing filter chips.
}

@Component({
  selector: 'va-filter-chip-container',
  templateUrl: './va-filter-chip-container.component.html',
  styleUrls: ['./va-filter-chip-container.component.scss'],
})
export class VaFilterChipContainerComponent {
  @Input() filters: VaFilterChipConfig[];
  @Input() config?: VaFilterChipContainerConfig;
  // eslint-disable-next-line @angular-eslint/no-output-on-prefix
  @Output() onFilterClick = new EventEmitter();
  // eslint-disable-next-line @angular-eslint/no-output-on-prefix
  @Output() onFilterDisable = new EventEmitter();
  // eslint-disable-next-line @angular-eslint/no-output-on-prefix
  @Output() onFilterDataChange = new EventEmitter();

  constructor(private dialog: MatDialog) {}

  /**
   * Disable all filters that can be disabled.
   */
  clearAll(): void {
    this.filters.filter((filter) => filter.enabled).forEach((filter) => this.onDisableFilter(filter.id));
  }

  /**
   * Disable a filter from the filters list.
   *
   * @param filterId - ID of the filter to disable
   */
  onDisableFilter(filterId: string): void {
    const filterToDisable = this.filters.find((filter) => filter.id === filterId);
    if (!filterToDisable) {
      return;
    }

    if (filterToDisable.canDisable) {
      filterToDisable.enabled = false;
    }

    this.onFilterDisable.emit({
      id: filterToDisable.id,
    });
  }

  /**
   * When a filter item is picked from the dropdown, enable it.
   *
   * @param filterId - Id of the filter that was selecetd from the list.
   */
  onFilterMenuItemClick(filterId: string): void {
    const filterItem = this.filters.find((filter) => filter.id === filterId);
    if (!filterItem) {
      return;
    }

    filterItem.enabled = true;

    // Defer opening of the filter dropdown to ensure that filter chip is present in the DOM
    requestAnimationFrame(() => {
      this.onClickFilter(filterId);
    });
  }

  /**
   * Emits and event when a chip is clicked.
   *
   * @param filterId - ID of the filter that was clicked
   */
  onClickFilter(filterId: string): void {
    const filterItem = this.filters.find((filter) => filter.id === filterId);
    if (!filterItem) {
      return;
    }

    this.onFilterClick.emit({
      id: filterId,
    });

    if (filterItem.dialog) {
      this.openFilterDialog(filterItem);
    }
  }

  openFilterDialog(filterItem: VaFilterChipConfig): void {
    const { dialog, data, id } = filterItem;
    const { component, width, height } = dialog!;

    const dialogRef = this.dialog.open(component, { data: { ...data }, id, width, height });

    const closeSub = dialogRef.afterClosed().subscribe((res) => {
      closeSub.unsubscribe();

      if (typeof res !== 'undefined') {
        this.onFilterDataChange.emit({
          id,
          data: res.data,
        });
      }
    });
  }
}
